import { useMemo } from 'react';

import { type RouterOutputs, trpc } from '@/config/trpc';
import { type CaseRole } from '~/common/enums';
import { type Nullable } from '~/common/types';

import useDebounce from './UseDebounce';

const DEBOUNCE_DELAY_MS = 1000;

export type DuplicateReferenceCase =
  RouterOutputs['case']['relation']['duplicateReference'][number];

type UseDuplicateReferenceCasesParams = {
  enabled?: boolean;
  excludeCaseId?: Nullable<string>;
  reference?: Nullable<string>;
  role?: Nullable<CaseRole>;
};

/**
 * Looks up existing cases already using the given relation reference, to warn about accidental
 * duplicates. Scoped server-side to cases owned by the requesting team, since an owner can already
 * see every relation on its own cases - no company/contact identity needs to be passed in.
 *
 * The reference is debounced internally, so consumers can pass the raw (undebounced) form value.
 * The lookup only runs once a non-empty reference is available.
 *
 * @param params.enabled - Set to false to suspend the lookup entirely. Defaults to true.
 * @param params.excludeCaseId - Case to leave out of the results, typically the case being edited.
 * @param params.reference - Relation reference to look for.
 * @param params.role - Narrows the lookup to relations holding this case role.
 * @returns The matching cases and whether a lookup is in flight.
 */
const useDuplicateReferenceCases = ({
  enabled = true,
  excludeCaseId,
  reference,
  role,
}: UseDuplicateReferenceCasesParams) => {
  const debouncedReference = useDebounce(reference?.trim() ?? '', DEBOUNCE_DELAY_MS);

  const { data, isFetching } = trpc.case.relation.duplicateReference.useQuery(
    {
      excludeCaseId: excludeCaseId ?? undefined,
      reference: debouncedReference,
      role: role ?? undefined,
    },
    {
      enabled: enabled && !!debouncedReference,
    },
  );

  const cases = useMemo(() => data ?? [], [data]);

  return { cases, isFetching };
};

export default useDuplicateReferenceCases;
